import React, { useState, useEffect, memo } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * SystemHealth - Widget "Santé système" pour Admin
 */
const SystemHealth = memo(({ token, onNavigate }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/perf/metrics`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();
        setMetrics(data.success ? data.data : null);
      } catch (err) {
        console.error('Erreur fetch metrics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    // Rafraîchir toutes les 60 secondes
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, [token]);

  const getStatusColor = (value, thresholds) => {
    if (value <= thresholds.good) return '#28a745';
    if (value <= thresholds.warning) return '#ffc107';
    return '#dc3545';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Santé système</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Santé système</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  // Valeurs par défaut si les métriques ne sont pas disponibles
  const avgResponseTime = Number(metrics?.avgResponseTime || metrics?.average_response_time) || 0;
  const requestsPerHour = Number(metrics?.requestsPerHour || metrics?.requests_per_hour) || 0;
  // errorRate peut être "0.50%" (string) ou un number
  const rawErrorRate = metrics?.errorRate || metrics?.error_rate || 0;
  const errorRate = typeof rawErrorRate === 'string'
    ? parseFloat(rawErrorRate.replace('%', '')) || 0
    : Number(rawErrorRate) || 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Santé système</h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('analytics')}
        >
          Voir analytics
        </button>
      </div>

      <div style={styles.metricsGrid}>
        <div style={styles.metricItem}>
          <span style={styles.metricLabel}>API</span>
          <span
            style={{
              ...styles.metricValue,
              color: getStatusColor(avgResponseTime, { good: 100, warning: 300 })
            }}
          >
            {Math.round(avgResponseTime)}ms
          </span>
          <span style={styles.metricSub}>moy.</span>
        </div>

        <div style={styles.metricItem}>
          <span style={styles.metricLabel}>Req/h</span>
          <span style={styles.metricValue}>
            {requestsPerHour.toLocaleString()}
          </span>
          <span style={styles.metricSub}>requêtes</span>
        </div>

        <div style={styles.metricItem}>
          <span style={styles.metricLabel}>Erreurs</span>
          <span
            style={{
              ...styles.metricValue,
              color: getStatusColor(errorRate, { good: 1, warning: 5 })
            }}
          >
            {errorRate.toFixed(1)}%
          </span>
          <span style={styles.metricSub}>taux</span>
        </div>
      </div>

      {/* Indicateur de statut global */}
      <div style={styles.statusBar}>
        <span style={styles.statusDot(errorRate < 1 && avgResponseTime < 200 ? '#28a745' : errorRate < 5 ? '#ffc107' : '#dc3545')}></span>
        <span style={styles.statusText}>
          {errorRate < 1 && avgResponseTime < 200 ? 'Système opérationnel' :
           errorRate < 5 ? 'Performances dégradées' : 'Problèmes détectés'}
        </span>
      </div>
    </div>
  );
});

SystemHealth.displayName = 'SystemHealth';

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    height: '100%',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '16px'
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  },
  metricLabel: {
    fontSize: '0.75rem',
    color: '#6c757d',
    fontWeight: '500',
    textTransform: 'uppercase',
    marginBottom: '4px'
  },
  metricValue: {
    fontSize: '1.4rem',
    fontWeight: '700',
    color: '#1a1a2e'
  },
  metricSub: {
    fontSize: '0.75rem',
    color: '#6c757d',
    marginTop: '2px'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px'
  },
  statusDot: (color) => ({
    width: '10px',
    height: '10px',
    backgroundColor: color,
    borderRadius: '50%'
  }),
  statusText: {
    fontSize: '0.9rem',
    color: '#495057'
  }
};

export default SystemHealth;
